import { observer } from "mobx-react-lite";
import React from "react";
import { ComponentItemProps } from "./ComponentTree";
import { ComponentSpec } from ".";
import { getComponent } from "./editable_index";

export interface IEditableComponent<S> {
  component: React.FC<{ spec: S }>;
  properties: React.FC<{ spec: S }>;
  siteProperties?: React.FC<{ spec: S }>;
  item: React.FC<ComponentItemProps<S>>;
  new?: React.FC<{}>;
}

export interface DragSpec {
  spec: ComponentSpec;
}

export const Component = observer((props: { spec: ComponentSpec }) => {
  const Comp = getComponent(props.spec.type).component;
  return <Comp spec={props.spec} />;
});

export const ComponentProps = observer((props: { spec: ComponentSpec }) => {
  const Comp = getComponent(props.spec.type).properties;
  return <Comp spec={props.spec} />;
});

export const ComponentItem = observer(
  (props: ComponentItemProps<ComponentSpec>) => {
    const Comp = getComponent(props.spec.type).item;
    return <Comp {...props} />;
  }
);

export const PageProps = observer(
  (props: { spec: ComponentSpec }): React.ReactElement => {
    let items: JSX.Element[] = [];

    // Helper function to recursively find components with siteProperties
    const findComponentsWithSiteProperties = (component: ComponentSpec) => {
      const Comp = getComponent(component.type).siteProperties;
      if (Comp) {
        items.push(<Comp spec={component} />);
      }

      // Check for children in standard "content.children" location
      if (component.content?.children) {
        for (const child of component.content.children) {
          findComponentsWithSiteProperties(child);
        }
      }

      // Check for children in "child.children" pattern
      if (component.child?.children) {
        for (const child of component.child.children) {
          findComponentsWithSiteProperties(child);
        }
      }

      // Check for direct "child" property that might be a component
      if (
        component.child &&
        !component.child.children &&
        component.child.type
      ) {
        findComponentsWithSiteProperties(component.child);
      }
    };

    // Start recursive search from the root component
    findComponentsWithSiteProperties(props.spec);

    return <>{items}</>;
  }
);
